import { useState } from "react";
import moment from "moment-timezone";
import Button from "components/ui/Button";
import SearchSelect from "components/ui/SearchSelect";
import {
  useAdjustUserCreditsMutation,
  useGetUserCreditsQuery,
} from "apis/user";

const CREDIT_TYPE_OPTIONS = [
  { value: "photo", label: "Photo edit credits" },
  { value: "editorReview", label: "Editor review credits" },
];

const MODE_OPTIONS = [
  { value: "add", label: "Add credits" },
  { value: "remove", label: "Remove credits" },
];

const TYPE_LABELS = {
  purchase: "Purchase",
  consumption: "Used",
  refund: "Refund",
  adminAdjustment: "Admin",
  transferOut: "Allocated out",
  transferIn: "Allocated in",
};

const inputClass =
  "w-full px-3 py-2 border border-darkgray rounded-md shadow-sm focus:outline-none focus:ring-2 focus:darkgray focus:border-darkgray";

const newRequestId = () =>
  window.crypto?.randomUUID?.() ||
  `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const UserCredits = ({ user, onClose }) => {
  const [creditType, setCreditType] = useState("photo");
  const [mode, setMode] = useState("add");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  // One id per intended adjustment — a double click or retry is applied once.
  const [requestId, setRequestId] = useState(newRequestId);

  const { data, isLoading } = useGetUserCreditsQuery(user?._id);
  const { mutate: adjustCredits, isPending } = useAdjustUserCreditsMutation();

  const balance = data?.balances?.[creditType]?.balance ?? 0;
  const qty = Number(amount);
  const isValid = Number.isInteger(qty) && qty > 0 && reason.trim();
  const after = mode === "add" ? balance + qty : balance - qty;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isValid || after < 0) return;
    adjustCredits(
      {
        id: user._id,
        creditType,
        amount: mode === "add" ? qty : -qty,
        reason: reason.trim(),
        requestId,
      },
      {
        onSuccess: () => {
          setAmount("");
          setReason("");
          setRequestId(newRequestId());
        },
      }
    );
  };

  const transactions = (data?.transactions || []).filter(
    (t) => t.creditType === creditType
  );

  return (
    <div className="space-y-5">
      <div>
        <p className="font-medium">{user?.name}</p>
        <p className="text-sm text-darkgray">{user?.email}</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {CREDIT_TYPE_OPTIONS.map((opt) => (
          <div
            key={opt.value}
            className="border border-mediumgray rounded-md p-3 bg-lightgray"
          >
            <p className="text-xs text-darkgray">{opt.label}</p>
            <p className="text-2xl font-semibold">
              {isLoading ? "…" : data?.balances?.[opt.value]?.balance ?? 0}
            </p>
          </div>
        ))}
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="form-label">Credit type</label>
            <SearchSelect
              options={CREDIT_TYPE_OPTIONS}
              value={CREDIT_TYPE_OPTIONS.find((o) => o.value === creditType)}
              onChange={(opt) => setCreditType(opt?.value ?? "photo")}
              searchable={false}
            />
          </div>
          <div>
            <label className="form-label">Action</label>
            <SearchSelect
              options={MODE_OPTIONS}
              value={MODE_OPTIONS.find((o) => o.value === mode)}
              onChange={(opt) => setMode(opt?.value ?? "add")}
              searchable={false}
            />
          </div>
        </div>

        <div>
          <label className="form-label">Amount</label>
          <input
            type="number"
            min="1"
            max="1000"
            step="1"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="e.g. 20"
            className={inputClass}
          />
          {qty > 0 && (
            <p
              className={`text-sm mt-1 ${
                after < 0 ? "text-red-500" : "text-darkgray"
              }`}
            >
              {after < 0
                ? `Only ${balance} available to remove`
                : `Balance after: ${balance} → ${after}`}
            </p>
          )}
        </div>

        <div>
          <label className="form-label">Reason *</label>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Goodwill credit for re-edit"
            className={inputClass}
          />
        </div>

        <div className="flex justify-end gap-2">
          <Button onClick={onClose} className="gray-button">
            Close
          </Button>
          <Button
            type="submit"
            className="black-button"
            loading={isPending}
            disabled={isPending || !isValid || after < 0}
          >
            {mode === "add" ? "Add credits" : "Remove credits"}
          </Button>
        </div>
      </form>

      <div>
        <p className="font-medium mb-2">Recent activity</p>
        {transactions.length === 0 ? (
          <p className="text-sm text-darkgray">No activity yet.</p>
        ) : (
          <div className="max-h-56 overflow-y-auto border border-mediumgray rounded-md">
            <table className="w-full text-sm">
              <thead className="bg-lightgray text-left">
                <tr>
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2 text-right">Balance</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((t) => (
                  <tr key={t._id} className="border-t border-mediumgray">
                    <td className="px-3 py-2 whitespace-nowrap">
                      {moment(t.createdAt)
                        .tz("Australia/Sydney")
                        .format("D MMM YYYY")}
                    </td>
                    <td
                      className="px-3 py-2"
                      title={t.metadata?.reason || t.description}
                    >
                      {TYPE_LABELS[t.type] || t.type}
                    </td>
                    <td
                      className={`px-3 py-2 text-right ${
                        t.amount < 0 ? "text-red-500" : "text-green-600"
                      }`}
                    >
                      {t.amount > 0 ? `+${t.amount}` : t.amount}
                    </td>
                    <td className="px-3 py-2 text-right">{t.balanceAfter}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserCredits;
